import { useMemo, useState } from 'react';

import { DualAxes } from '@ant-design/charts';
import { Card, Empty, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';

import { formatLongDate } from 'scenes/RouteReport/utils/time';

import {
  TiendaPerfectaVisitHistoryStop,
  TiendaPerfectaVisitHistorySummary,
} from 'models/TiendaPerfecta';

import { useChartClick } from '../hooks/useChartClick';
import {
  buildDayPoints,
  buildMaterialDays,
  dayTickLabel,
  niceMax,
  toStackRows,
} from '../utils/charts';
import { formatInt, roundedPercentages, zoneLabel } from '../utils/format';
import {
  LegendItem,
  MaterialGroup,
  bucketTotals,
  buildLegend,
  groupUnits,
} from '../utils/materialCharts';
import {
  VisitSelection,
  aggregateStops,
  stopMatchesSelection,
} from '../utils/selection';
import { SelectionScope, isDimmed } from '../utils/selectionScope';

const I18N_BASE = 'scenes.visit_history.material';
const DIMMED_OPACITY = 0.45;
const DENSE_FROM_DAYS = 15;

type GroupBy = 'van' | 'zone';

interface VisitMaterialCardsProps {
  summary: TiendaPerfectaVisitHistorySummary;
  stops: TiendaPerfectaVisitHistoryStop[];
  /** Every day of the range, also those without routes. */
  days: string[];
  selection: VisitSelection;
  scope: SelectionScope;
  onSelect: (selection: NonNullable<VisitSelection>) => void;
  timeZone: string;
}

const Legend = ({ items }: { items: LegendItem[] }) => (
  <div className="visit-history-legend">
    {items.map((item) => (
      <span key={item.materialId}>
        <i style={{ background: item.color }} />
        {item.name}
      </span>
    ))}
  </div>
);

/**
 * The three material cards. Charts always show the whole range and dim what
 * the selection leaves out; only the totals follow the selection, recomputed
 * from the stops so they match the Material KPI.
 */
function VisitMaterialCards({
  summary,
  stops,
  days,
  selection,
  scope,
  onSelect,
  timeZone,
}: VisitMaterialCardsProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const [groupBy, setGroupBy] = useState<GroupBy>('van');
  const { materials } = summary;

  // Built from the full range so each material keeps its color.
  const legend = useMemo(
    () =>
      buildLegend(
        materials.totals,
        materials.catalog,
        t(`${I18N_BASE}.others`)
      ),
    [materials.totals, materials.catalog, t]
  );

  const materialDays = useMemo(() => {
    const points = buildDayPoints(days, summary.byDay);
    const groups = groupUnits(
      materials.byDay.map((r) => ({
        key: r.date,
        materialId: r.materialId,
        units: r.units,
      })),
      legend,
      (key) => key
    );

    return buildMaterialDays(points, groups);
  }, [days, summary.byDay, materials.byDay, legend]);

  const groups: MaterialGroup[] = useMemo(() => {
    const names = new Map<string, string>();
    if (groupBy === 'van') {
      summary.vans.forEach((row) => names.set(row.van.id, row.van.name));
    } else {
      // The zone NAME travels with each `byZone` row; an empty one gets the
      // i18n fallback, never the raw id.
      materials.byZone.forEach((row) =>
        names.set(
          row.zoneId,
          zoneLabel(
            { name: row.zoneName },
            t('scenes.visit_history.zone_unnamed')
          )
        )
      );
    }
    const rows =
      groupBy === 'van'
        ? materials.byVan.map((r) => ({ ...r, key: r.vanId }))
        : materials.byZone.map((r) => ({ ...r, key: r.zoneId }));

    return groupUnits(rows, legend, (key) => names.get(key) ?? key).sort(
      (a, b) => b.total - a.total || a.label.localeCompare(b.label)
    );
  }, [groupBy, summary.vans, materials.byVan, materials.byZone, legend, t]);

  const selectedTotals = useMemo(
    () =>
      selection
        ? aggregateStops(
            stops.filter((stop) => stopMatchesSelection(stop, selection)),
            timeZone
          ).materialTotals
        : materials.totals,
    [selection, stops, timeZone, materials.totals]
  );
  const totalRows = useMemo(
    () => bucketTotals(selectedTotals, legend),
    [selectedTotals, legend]
  );

  const onDayClick = useChartClick((datum) => {
    const day = materialDays.find((d) => d.date === datum.date);
    if (day && !day.empty) onSelect({ kind: 'day', date: datum.date });
  });

  const dayConfig = useMemo(() => {
    const dates = days;
    const dense = dates.length >= DENSE_FROM_DAYS;
    const yMax = niceMax(Math.max(0, ...materialDays.map((d) => d.total)));
    const xScale = { domain: dates };
    const yScale = { domain: [0, yMax], nice: false };
    const byDate = new Map(materialDays.map((d) => [d.date, d]));

    return {
      xField: 'date',
      legend: false,
      onEvent: onDayClick,
      children: [
        {
          type: 'interval' as const,
          data: toStackRows(materialDays, legend),
          yField: 'value',
          colorField: 'material',
          stack: true,
          style: {
            maxWidth: 36,
            fillOpacity: (d: any) =>
              isDimmed(scope, 'date', d.date) ? DIMMED_OPACITY : 1,
          },
          scale: {
            x: xScale,
            y: yScale,
            color: {
              domain: legend.map((l) => l.name),
              range: legend.map((l) => l.color),
            },
          },
          axis: {
            x: {
              labelAutoRotate: false,
              labelFormatter: (date: string) => {
                const day = dayTickLabel(date, language, dense);

                return byDate.get(date)?.empty
                  ? `${day}\n${t('scenes.visit_history.days_chart.no_routes')}`
                  : day;
              },
            },
            y: {
              position: 'left',
              labelFormatter: (v: number) => formatInt(v, language),
            },
          },
          tooltip: {
            title: (d: any) => formatLongDate(d.date),
            items: [
              (d: any) => ({
                name: d.material,
                value: `${formatInt(d.value, language)} ${t(
                  `${I18N_BASE}.totals.units_short`
                )}`,
                color: legend.find((l) => l.name === d.material)?.color,
              }),
            ],
          },
        },
        {
          // Invisible line whose only job is to print each day's total.
          type: 'line' as const,
          data: materialDays.map((d) => ({ date: d.date, total: d.total })),
          yField: 'total',
          style: { lineWidth: 0 },
          scale: { x: xScale, y: yScale },
          axis: { y: false },
          labels: [
            {
              text: (d: any) =>
                d.total > 0 ? formatInt(d.total, language) : '',
              position: 'top',
              dy: -8,
              fontSize: 11,
              fontWeight: 600,
            },
          ],
          tooltip: false,
        },
      ],
    };
  }, [days, materialDays, legend, scope, language, t, onDayClick]);

  if (legend.length === 0) {
    return (
      <Card>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(`${I18N_BASE}.empty`)}
        />
      </Card>
    );
  }

  const maxGroup = Math.max(1, ...groups.map((g) => g.total));
  const totalUnits = totalRows.reduce((sum, row) => sum + row.units, 0);
  const maxRow = Math.max(1, ...totalRows.map((row) => row.units));
  const shares = useMemo(
    () => roundedPercentages(totalRows.map((row) => row.units)),
    [totalRows]
  );
  const materialCount = selectedTotals.filter((m) => m.units > 0).length;
  const legendById = new Map(legend.map((l) => [l.materialId, l]));
  const unitsShort = t(`${I18N_BASE}.totals.units_short`);

  return (
    <div className="visit-history-material">
      <Card title={t(`${I18N_BASE}.by_day.title`)}>
        <Legend items={legend} />
        <DualAxes {...(dayConfig as any)} height={300} />
      </Card>

      <Card
        title={t(`${I18N_BASE}.by_group.title`)}
        extra={
          <Segmented
            size="small"
            value={groupBy}
            onChange={(value) => setGroupBy(value as GroupBy)}
            options={[
              { label: t(`${I18N_BASE}.by_group.van`), value: 'van' },
              { label: t(`${I18N_BASE}.by_group.zone`), value: 'zone' },
            ]}
          />
        }
      >
        <Legend items={legend} />
        <div className="visit-history-mat-groups">
          {groups.map((group) => (
            <button
              type="button"
              key={group.key}
              className={`visit-history-mat-groups__row${
                isDimmed(scope, groupBy, group.key) ? ' is-dimmed' : ''
              }`}
              onClick={() =>
                onSelect(
                  groupBy === 'van'
                    ? { kind: 'van', vanId: group.key }
                    : { kind: 'zone', zoneId: group.key }
                )
              }
            >
              <span
                className="visit-history-mat-groups__name"
                title={group.label}
              >
                {group.label}
              </span>
              <span
                className="visit-history-mat-groups__track"
                style={{ width: `${(group.total / maxGroup) * 100}%` }}
              >
                {group.parts.map((part) => (
                  <span
                    key={part.materialId}
                    style={{
                      width: `${(part.units / group.total) * 100}%`,
                      background: legendById.get(part.materialId)?.color,
                    }}
                    title={`${
                      legendById.get(part.materialId)?.name
                    }: ${formatInt(part.units, language)} ${unitsShort}`}
                  />
                ))}
              </span>
              <span className="visit-history-mat-groups__total">
                {formatInt(group.total, language)}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card title={t(`${I18N_BASE}.totals.title`)}>
        <div className="visit-history-mat-totals">
          <div className="visit-history-mat-totals__big">
            {formatInt(totalUnits, language)}
            <small>{t(`${I18N_BASE}.totals.units`)}</small>
          </div>
          <div className="visit-history-mat-totals__count">
            {t(`${I18N_BASE}.totals.materials`, { count: materialCount })}
          </div>
          {totalRows.map((row, index) => (
            <div key={row.materialId} className="visit-history-mat-totals__row">
              <span className="visit-history-mat-totals__name">
                <i style={{ background: row.color }} />
                {row.name}
              </span>
              <span className="visit-history-mat-totals__value">
                <b>{formatInt(row.units, language)}</b> {unitsShort} &middot;{' '}
                {shares[index]}%
              </span>
              <span className="visit-history-mat-totals__bar">
                <span
                  style={{
                    width: `${(row.units / maxRow) * 100}%`,
                    background: row.color,
                  }}
                />
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

export default VisitMaterialCards;
